/**
 * ChangePasswordForm Component
 *
 * Handles first-time login password change flow.
 * When admin creates user and provides initial credentials,
 * user must change password before accessing dashboard.
 */

import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { FormField } from '../ui/FormField';
import { Button } from '../ui/Button';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { changePasswordFirstLogin, setAuthStep } from '../../store/authSlice';

interface ChangePasswordFormProps {
  onSuccess?: () => void;
  onShowToast?: (message: string) => void;
}

interface ChangePasswordFormData {
  newPassword: string;
  confirmPassword: string;
}

interface ChangePasswordFormErrors {
  newPassword?: string;
  confirmPassword?: string;
}

export const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({
  onSuccess,
  onShowToast,
}) => {
  const dispatch = useAppDispatch();
  const { isLoading, error: authError, passwordPolicy } = useAppSelector((state) => state.auth);

  // Get email and reason from sessionStorage (set by LoginForm)
  const email = sessionStorage.getItem('change_password_email') || '';
  const reason = sessionStorage.getItem('change_password_reason') || 'first-time'; // 'first-time' or 'expired'

  const [formData, setFormData] = useState<ChangePasswordFormData>({
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<ChangePasswordFormErrors>({});
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Validation Functions
  const validatePassword = (password: string): string | undefined => {
    if (!password) return 'Password is required';

    if (password.length < passwordPolicy.minLength) {
      return `Password must be at least ${passwordPolicy.minLength} characters`;
    }
    if (password.length > passwordPolicy.maxLength) {
      return `Password must be no more than ${passwordPolicy.maxLength} characters`;
    }

    // Angular pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&])(?!.*\s).{8,}$/
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[$@$!%*?&]/.test(password);
    const hasNoSpaces = !/\s/.test(password);

    if (!hasLowercase || !hasUppercase || !hasNumber || !hasSpecialChar || !hasNoSpaces) {
      return 'Password must contain uppercase, lowercase, number, special character ($@$!%*?&), and no spaces';
    }

    return undefined;
  };

  const validateConfirmPassword = (password: string, confirmPassword: string): string | undefined => {
    if (!confirmPassword) return 'Please confirm your password';
    if (password !== confirmPassword) return 'Passwords do not match';
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: ChangePasswordFormErrors = {};

    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) newErrors.newPassword = passwordError;

    const confirmPasswordError = validateConfirmPassword(formData.newPassword, formData.confirmPassword);
    if (confirmPasswordError) newErrors.confirmPassword = confirmPasswordError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit Handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      console.log('[ChangePasswordForm] Validation failed:', errors);
      return;
    }

    if (!email) {
      setErrors({ newPassword: 'User email not found. Please login again.' });
      return;
    }

    console.log('[ChangePasswordForm] Submitting password change...');

    try {
      const result = await dispatch(changePasswordFirstLogin({
        email: email,
        password: formData.newPassword,
      }));

      if (changePasswordFirstLogin.fulfilled.match(result)) {
        console.log('[ChangePasswordForm] ✅ Password changed successfully');

        onShowToast?.('Password changed successfully. Please login with your new password.');

        // Clear session storage (token, user info, email, reason, and password expiry)
        sessionStorage.removeItem('finpro_k_Authorization');
        sessionStorage.removeItem('loggedUserInfo');
        sessionStorage.removeItem('change_password_email');
        sessionStorage.removeItem('change_password_reason');
        sessionStorage.removeItem('passwordExpiry');

        onSuccess?.();

        console.log('[ChangePasswordForm] Redirecting to login...');
      } else if (changePasswordFirstLogin.rejected.match(result)) {
        console.error('[ChangePasswordForm] ❌ Password change failed:', result.payload);
        setErrors({ newPassword: result.payload as string });
      }
    } catch (err: any) {
      console.error('[ChangePasswordForm] ❌ Unexpected error:', err);
      setErrors({ newPassword: 'An unexpected error occurred. Please try again.' });
    }
  };

  const handleFieldChange = (field: keyof ChangePasswordFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => e.preventDefault();
  const handleCopy = (e: React.ClipboardEvent<HTMLInputElement>) => e.preventDefault();
  const handleCut = (e: React.ClipboardEvent<HTMLInputElement>) => e.preventDefault();

  const handleBackToLogin = () => {
    // Clear all session storage
    sessionStorage.removeItem('finpro_k_Authorization');
    sessionStorage.removeItem('loggedUserInfo');
    sessionStorage.removeItem('change_password_email');
    sessionStorage.removeItem('change_password_reason');
    sessionStorage.removeItem('passwordExpiry');

    // Navigate back to login
    dispatch(setAuthStep('login'));
  };

  return (
    <div className="w-full h-full flex flex-col min-h-0">
      <p className="text-sm mb-4" style={{ color: '#0F3340' }}>
        {reason === 'expired'
          ? 'Your password has expired. For security reasons, you must change your password before accessing the dashboard.'
          : 'For security reasons, you must change your password before accessing the dashboard.'}
      </p>

      <div className="flex-1 flex flex-col min-h-0">
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
          <div className="space-y-6 mt-8">
            {/* Email Field (readonly) */}
            <FormField
              label="EMAIL"
              name="email"
              type="email"
              value={email || ''}
              onChange={() => {}}
              placeholder="Your email address"
              autoComplete="email"
              disabled
              required
            />

            {/* New Password Field */}
            <div className="space-y-1 mt-4">
              <label htmlFor="newPassword" className="block uppercase" style={{
                color: '#0F3340',
                fontFamily: 'Helvetica Neue, sans-serif',
                fontWeight: 500,
                fontSize: '1.125rem',
                lineHeight: '100%',
                letterSpacing: '0.02em'
              }}>
                NEW PASSWORD
              </label>
              <div className="relative">
                <input
                  id="newPassword"
                  name="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={formData.newPassword}
                  onChange={(e) => handleFieldChange('newPassword', e.target.value)}
                  placeholder={`Min ${passwordPolicy.minLength}, max ${passwordPolicy.maxLength} characters`}
                  autoComplete="new-password"
                  required
                  minLength={passwordPolicy.minLength}
                  maxLength={passwordPolicy.maxLength}
                  onPaste={handlePaste}
                  onCopy={handleCopy}
                  onCut={handleCut}
                  className={`
                    w-full pl-3 ${formData.newPassword ? 'pr-12' : 'pr-4'} py-3 rounded-lg transition-colors bg-white
                    placeholder-gray-400
                    ${errors.newPassword
                      ? 'focus:outline-none focus:ring-2 focus:ring-red-500'
                      : 'focus:outline-none focus:ring-2 focus:ring-[#1B84C6]'}
                  `}
                  style={{
                    border: errors.newPassword ? '1px solid #fca5a5' : '1px solid #D9F0FF'
                  }}
                />
                {formData.newPassword && (
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    style={{
                      right: '12px',
                      background: 'none',
                      border: 'none',
                      outline: 'none',
                      boxShadow: 'none',
                      padding: '4px',
                      cursor: 'pointer',
                      zIndex: 10
                    }}
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                )}
              </div>
              {errors.newPassword && <p className="text-sm" style={{ color: '#CA7373' }}>{errors.newPassword}</p>}
              {!errors.newPassword && (
                <p className="text-xs" style={{ color: '#6B7280' }}>
                  Must contain uppercase, lowercase, number, and special character ($@$!%*?&)
                </p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-1">
              <label htmlFor="confirmPassword" className="block uppercase" style={{
                color: '#0F3340',
                fontFamily: 'Helvetica Neue, sans-serif',
                fontWeight: 500,
                fontSize: '1.125rem',
                lineHeight: '100%',
                letterSpacing: '0.02em'
              }}>
                CONFIRM PASSWORD
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => handleFieldChange('confirmPassword', e.target.value)}
                  placeholder="Re-enter your new password"
                  autoComplete="new-password"
                  required
                  minLength={passwordPolicy.minLength}
                  maxLength={passwordPolicy.maxLength}
                  onPaste={handlePaste}
                  onCopy={handleCopy}
                  onCut={handleCut}
                  className={`
                    w-full pl-3 ${formData.confirmPassword ? 'pr-12' : 'pr-4'} py-3 rounded-lg transition-colors bg-white
                    placeholder-gray-400
                    ${errors.confirmPassword
                      ? 'focus:outline-none focus:ring-2 focus:ring-red-500'
                      : 'focus:outline-none focus:ring-2 focus:ring-[#1B84C6]'}
                  `}
                  style={{
                    border: errors.confirmPassword ? '1px solid #fca5a5' : '1px solid #D9F0FF'
                  }}
                />
                {formData.confirmPassword && (
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    style={{
                      right: '12px',
                      background: 'none',
                      border: 'none',
                      outline: 'none',
                      boxShadow: 'none',
                      padding: '4px',
                      cursor: 'pointer',
                      zIndex: 10
                    }}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                )}
              </div>
              {errors.confirmPassword && <p className="text-sm" style={{ color: '#CA7373' }}>{errors.confirmPassword}</p>}
            </div>

            {authError && (
              <div className="text-sm" style={{ color: '#CA7373' }}>
                {authError}
              </div>
            )}
          </div>

          <div className="flex-1"></div>

          <div className="pb-4">
            <Button
              type="submit"
              loading={isLoading}
              disabled={!formData.newPassword || !formData.confirmPassword}
              fullWidth
              size="lg"
              variant="gradient"
            >
              Change Password
            </Button>

            {/* Back to Login Link - matching LoginForm "Forgot Password?" style */}
            <div className="text-center text-sm" style={{ marginTop: '1rem' }}>
              <span
                onClick={handleBackToLogin}
                className="text-sm font-medium cursor-pointer hover:underline"
                style={{
                  color: '#0074A5',
                  outline: 'none',
                  border: 'none',
                  background: 'none'
                }}
                tabIndex={0}
                role="button"
              >
                Back to Login
              </span>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
